import type Vendor from "../models/Vendor";
import type { VendorQuote } from "../models/VendorQuote";

export const QUICKBOOKS_LINKED_VENDOR_REQUIRED_MSG =
  "Link a vendor from the directory on the selected quote before sending to QuickBooks";

const normalizeOptionalText = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/** Linked vendor from AppContext when quote has a positive vendorId. */
export function vendorForQuote(
  quote: VendorQuote,
  vendors: Vendor[]
): Vendor | undefined {
  if (quote.vendorId == null || quote.vendorId <= 0) return undefined;
  return vendors.find((v) => v.id === quote.vendorId);
}

export function vendorQuoteHasLinkedVendor(
  quote: VendorQuote
): quote is VendorQuote & { vendorId: number } {
  return quote.vendorId != null && quote.vendorId > 0;
}

/**
 * Vendor label for QuickBooks PO/estimate: directory name, then quote.vendorName when linked.
 * Matches backend qb-export-context resolution.
 */
export function resolveQuickBooksVendorLabel(
  quote: VendorQuote,
  vendors: Vendor[]
): string | null {
  if (!vendorQuoteHasLinkedVendor(quote)) return null;
  const fromDirectory = vendorForQuote(quote, vendors)?.name?.trim();
  if (fromDirectory) return fromDirectory;
  const fromQuote = (quote.vendorName || "").trim();
  return fromQuote || null;
}

/** Display/export: linked resolver, else free-text quote.vendorName. */
export function vendorDisplayName(quote: VendorQuote, vendors: Vendor[]): string {
  const resolved = resolveQuickBooksVendorLabel(quote, vendors);
  if (resolved) return resolved;
  return (quote.vendorName || "").trim();
}

export function vendorDisplayNameForQuickBooks(
  quote: VendorQuote,
  vendors: Vendor[]
): string | null {
  return resolveQuickBooksVendorLabel(quote, vendors);
}

export type QuickBooksVendorLineRef = {
  preferredVendor?: string;
  vendorAndPhone?: string;
  vendorId?: number | null;
  itemName?: string;
};

/** Whether a queue/PO line has a non-empty selected-vendor label for QuickBooks grouping. */
export function quickBooksVendorLabelIsPresent(
  item: QuickBooksVendorLineRef,
  vendors?: Vendor[]
): boolean {
  // PO/Bill vendor is the selected quote vendor — not RFQ Source (preferredVendor).
  if (normalizeOptionalText(item.vendorAndPhone)) return true;
  const vendorId = item.vendorId;
  if (vendors && vendorId != null && vendorId > 0) {
    const name = vendors.find((v) => v.id === vendorId)?.name?.trim();
    if (name) return true;
  }
  return false;
}

export function quickBooksMissingVendorError(itemName: string): string {
  const label = normalizeOptionalText(itemName) || "line";
  return `${QUICKBOOKS_LINKED_VENDOR_REQUIRED_MSG} (${label})`;
}

/** Throws before queueing if any PO/Bill line lacks a vendor label. */
export function assertQuickBooksVendorLabelsPresent(
  items: QuickBooksVendorLineRef[],
  vendors?: Vendor[]
): void {
  for (const item of items) {
    if (!quickBooksVendorLabelIsPresent(item, vendors)) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[QB Sync] Missing vendor label for line", {
          itemName: item.itemName,
          vendorId: item.vendorId,
          preferredVendor: item.preferredVendor,
          vendorAndPhone: item.vendorAndPhone,
        });
      }
      throw new Error(quickBooksMissingVendorError(item.itemName ?? ""));
    }
  }
}
